import { REELS_CREATOR_TOOL_ID } from '@/shared/utils/reels-creator';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ClapperboardIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { ROUTES } from '@/shared/config/routes';
import { StaggerItem } from '@/shared/ui/animations';
import {
  ListPageBody,
  ListPageLayout,
  PageHeader,
  PageHeaderIdentity,
  PageHeaderTitle,
  PageHeaderToolGroup,
} from '@/shared/ui/layout';

interface ReelsCreatorSubpageFrameProps {
  title: string;
  testId: string;
  bodyClassName?: string;
  children: ReactNode;
}

/**
 * The header and body frame of a page under Reels Creator, rendered once by the route from the
 * entry chunk, so the page's skeleton and then the page swap in under a header that stays. The
 * tool badge leads back to the Reels home.
 */
export const ReelsCreatorSubpageFrame = ({
  title,
  testId,
  bodyClassName,
  children,
}: ReelsCreatorSubpageFrameProps) => {
  const { t } = useTranslation('reelsCreator');
  const navigate = useNavigate();

  return (
    <div data-testid={testId} className="page-layout">
      <ListPageLayout>
        <StaggerItem index={0}>
          <PageHeader>
            <PageHeaderIdentity>
              <PageHeaderToolGroup
                toolId={REELS_CREATOR_TOOL_ID}
                category="create"
                icon={ClapperboardIcon}
                tool={t('reelsCreatorHomePage.title')}
                onBadgeClick={() => void navigate(ROUTES.REELS_CREATOR)}
              >
                <PageHeaderTitle>{title}</PageHeaderTitle>
              </PageHeaderToolGroup>
            </PageHeaderIdentity>
          </PageHeader>
        </StaggerItem>

        <ListPageBody>
          <StaggerItem
            index={3}
            className={twMerge('flex min-h-0 flex-1 flex-col overflow-hidden', bodyClassName)}
          >
            {children}
          </StaggerItem>
        </ListPageBody>
      </ListPageLayout>
    </div>
  );
};
